import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  CalendarDays,
  RefreshCw,
  Download,
  ClipboardList,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  Store,
  Layers
} from "lucide-react";

export default function StockCard() {
  // =========================
  // STATE
  // =========================
  const [loading, setLoading] = useState(false);  
  const [tanggalStok, setTanggalStok] = useState("");
  // State untuk menampung form input request produk
// Menggunakan objek {} agar tiap toko punya ruang inputnya masing-masing
const [requestQty, setRequestQty] = useState({});
const [requestKategori, setRequestKategori] = useState({});
const [submittingRequest, setSubmittingRequest] = useState(false);
  const [lastAudit, setLastAudit] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [openAction, setOpenAction] = useState(null);

  const [detailArtikel, setDetailArtikel] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [viewMode, setViewMode] = useState("summary");
  const [today, setToday] = useState({});

  // State Baru untuk Detail Popup Stok
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [summary, setSummary] = useState({
    stok_audit: 0,
    sj_masuk: 0,
    mutasi_masuk: 0,
    pbm_rebarcode: 0,
    tukar_size_in: 0,
    tukar_size_out: 0,
    ret_whs: 0,
    ret_promosi: 0,
    ret_rebarcode: 0,
    penjualan: 0,
    mutasi_keluar: 0,
    stok_akhir: 0,
  });

  const [storeStockList, setStoreStockList] = useState([]);
const [loadingStore, setLoadingStore] = useState(false);

// Mengambil data ketersediaan stok toko nasional tiap kali selectedProduct berubah
useEffect(() => {
  const fetchStoreStock = async () => {
    if (!selectedProduct?.barcode) {
      setStoreStockList([]);
      return;
    }
    
    try {
      setLoadingStore(true);
      // Sesuaikan URL endpoint ini dengan path routing backend Express Anda
      const res = await axios.get("/api/inventory/store-stock", {
        params: { barcode: selectedProduct.barcode }
      });
      if (res.data?.success) {
        setStoreStockList(res.data.data);
      }
    } catch (err) {
      console.error("Gagal memuat ketersediaan stok toko:", err);
      setStoreStockList([]);
    } finally {
      setLoadingStore(false);
    }
  };

  fetchStoreStock();
}, [selectedProduct]);


// State untuk pencarian nama toko di dalam modal
const [searchStoreQuery, setSearchStoreQuery] = useState("");

// State untuk menyimpan data toko yang dipilih untuk melihat history-nya
const [selectedStoreHistory, setSelectedStoreHistory] = useState(null);
const [historyStockList, setHistoryStockList] = useState([]);
const [loadingHistory, setLoadingHistory] = useState(false);

// Filter list toko berdasarkan input pencarian secara lokal
const filteredStoreStockList = useMemo(() => {
  return storeStockList.filter((store) =>
    store.nama_toko?.toLowerCase().includes(searchStoreQuery.toLowerCase())
  );
}, [storeStockList, searchStoreQuery]);

// Efek untuk memuat data history stok toko saat toko diklik

// =========================
// HANDLER SUBMIT REQUEST
// =========================
const handleSendRequest = async (e, store) => {
  e.preventDefault();
  e.stopPropagation();

  // 💡 Ambil nilai spesifik milik toko ini, jika belum diisi default ke kosong/Direct Sales
  const currentQty = requestQty[store.kode_toko] || "";
  const currentKategori = requestKategori[store.kode_toko] || "Direct Sales";

  const qtyInput = parseInt(currentQty, 10);

  if (!qtyInput || qtyInput <= 0) {
    alert("Jumlah qty yang direquest harus lebih dari 0!");
    return;
  }
  if (qtyInput > store.stok_akhir) {
    alert(`Qty tidak boleh melebihi sisa stok toko (${store.stok_akhir} Pcs)!`);
    return;
  }

  try {
    setSubmittingRequest(true);
    
    const payload = {
      barcode: selectedProduct.barcode,
      artikel: selectedProduct.artikel,
      kode_toko_asal: store.kode_toko,
      kode_toko_tujuan: localStorage.getItem("kode_toko"),
       id_sunfish: localStorage.getItem("id_sunfish"),
      qty: qtyInput,
      kategori: currentKategori, // Gunakan kategori spesifik toko
    };

    const res = await axios.post("/api/inventory/request-product", payload);

    if (res.data?.success) {
      alert(`🎉 Berhasil mengirim request sebanyak ${qtyInput} Pcs ke ${store.nama_toko}!`);
      
      // Reset inputan khusus untuk toko ini saja setelah sukses
      setRequestQty(prev => ({ ...prev, [store.kode_toko]: "" }));
    } else {
      alert(res.data?.message || "Gagal memproses request produk.");
    }
  } catch (err) {
    console.error(err);
    alert("Terjadi kesalahan sistem backend.");
  } finally {
    setSubmittingRequest(false);
  }
};

useEffect(() => {
  const fetchStoreHistory = async () => {
    if (!selectedStoreHistory || !selectedProduct?.barcode) return;

    try {
      setLoadingHistory(true);
      // Menembak query: SELECT * FROM cutoff_md WHERE kode_toko/nama_toko = ? AND barcode = ?
      const res = await axios.get("/api/inventory/store-history", {
        params: { 
          kode_toko: selectedStoreHistory.kode_toko, // atau nama_toko tergantung backend
          barcode: selectedProduct.barcode 
        }
      });
      if (res.data?.success) {
        setHistoryStockList(res.data.data || []);
      }
    } catch (err) {
      console.error("Gagal memuat history stok toko:", err);
      setHistoryStockList([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  fetchStoreHistory();
}, [selectedStoreHistory, selectedProduct]);

// Reset query pencarian toko setiap kali produk utama berganti/modal ditutup
useEffect(() => {
  setSearchStoreQuery("");
  setSelectedStoreHistory(null);
  setHistoryStockList([]);
}, [selectedProduct]);

  // =========================
  // FETCH API
  // =========================
  const getInventory = async () => {
    try {
      setLoading(true);
      const kode_toko = localStorage.getItem("kode_toko");
      const res = await axios.get(
        "/api/inventory/stock-card-nasional",
        {
          params: { kode_toko: kode_toko },
        }
      );

      const data = res.data;

      if (data) {
        setToday(data.today || {});
        setLastAudit(data.last_audit || null);
        setDetailArtikel(data.detail || data.detail_artikel || []);
        
        // Proteksi Optional Chaining agar bebas Blank Putih
        setSummary({
          stok_audit: data.summary?.stok_audit || 0,
          sj_masuk: data.summary?.sj_masuk || 0,
          mutasi_masuk: data.summary?.mutasi_masuk || 0,
          pbm_rebarcode: data.summary?.pbm_rebarcode || 0,
          tukar_size_in: data.summary?.tukar_size_in || 0,
          tukar_size_out: data.summary?.tukar_size_out || 0,
          ret_whs: data.summary?.ret_whs || 0,
          ret_promosi: data.summary?.ret_promosi || 0,
          ret_rebarcode: data.summary?.ret_rebarcode || 0,
          penjualan: data.summary?.penjualan || 0,
          mutasi_keluar: data.summary?.mutasi_keluar || 0,
          stok_akhir: data.summary?.stok_akhir || 0,
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getInventory();
  }, []);

  // =========================
  // FORMAT
  // =========================
  const formattedLastAuditDate = useMemo(() => {
    if (!lastAudit || !lastAudit.tanggal) return "Belum Pernah Audit";
    return new Date(lastAudit.tanggal).toLocaleDateString("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }, [lastAudit]);

  const handleCardExport = (type) => {
    if (!type) return;
    const kode_toko = localStorage.getItem("kode_toko");
    const dateFrom = lastAudit?.tanggal 
      ? new Date(lastAudit.tanggal).toISOString().split('T')[0] 
      : '2026-05-01';

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const dateTo = yesterday.toISOString().split('T')[0];

    window.open(
      `/api/inventory/export-${type}?kode_toko=${kode_toko}&dateFrom=${dateFrom}&dateTo=${dateTo}`,
      "_blank"
    );
  };

  const summaryStats = [
    { label: "Stok Audit", value: summary.stok_audit.toLocaleString(), color: "from-slate-700 to-slate-900", type: null },
    { label: "SJ Masuk", value: summary.sj_masuk.toLocaleString(), color: "from-green-500 to-emerald-500", type: "sj-masuk" },
    { label: "Mutasi Masuk", value: summary.mutasi_masuk.toLocaleString(), color: "from-green-500 to-emerald-500", type: "mutasi-masuk" },
    { label: "PBM Rebarcode", value: summary.pbm_rebarcode.toLocaleString(), color: "from-green-500 to-emerald-500", type: "pbm-rebarcode" },
    { label: "Tukar Size In", value: summary.tukar_size_in.toLocaleString(), color: "from-green-500 to-emerald-500", type: "tukar-size-in" },
    { label: "Penjualan", value: summary.penjualan.toLocaleString(), color: "from-red-500 to-rose-500", type: "penjualan" },
    { label: "Mutasi Keluar", value: summary.mutasi_keluar.toLocaleString(), color: "from-red-500 to-rose-500", type: "mutasi-keluar" },
    { label: "Return WHS", value: summary.ret_whs.toLocaleString(), color: "from-red-500 to-rose-500", type: "ret-whs" },
    { label: "Return Promosi", value: summary.ret_promosi.toLocaleString(), color: "from-red-500 to-rose-500", type: "ret-promosi" },
    { label: "PBK Rebarcode", value: summary.ret_rebarcode.toLocaleString(), color: "from-red-500 to-rose-500", type: "ret-rebarcode" },
    { label: "Tukar Size Out", value: summary.tukar_size_out.toLocaleString(), color: "from-red-500 to-rose-500", type: "tukar-size-out" },
    { label: "Stok Akhir", value: summary.stok_akhir.toLocaleString(), color: "from-slate-700 to-slate-900", type: null },
  ];

  const todayStats = [
    { label: "Stok Awal", value: summary.stok_akhir.toLocaleString(), color: "from-slate-700 to-slate-900" }
  ];

  // =========================
  // DATA FILTER & PAGINATION
  // =========================
  const filteredArtikel = useMemo(() => {
    return detailArtikel.filter((item) => {
      const matchQuery = searchQuery.toLowerCase();
      return (
        item.artikel?.toLowerCase().includes(matchQuery) ||
        item.barcode?.toLowerCase().includes(matchQuery) ||
        item.dept?.toLowerCase().includes(matchQuery) ||
        item.prinsipal?.toLowerCase().includes(matchQuery)
      );
    });
  }, [detailArtikel, searchQuery]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredArtikel.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredArtikel, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredArtikel.length / itemsPerPage);

  const handleExport = async () => {
    try {
      const kode_toko = localStorage.getItem("kode_toko");
      window.open(
        `/api/inventory/export-stock-card?kode_toko=${kode_toko}&dateFrom=${dateFrom}&dateTo=${dateTo}`,
        "_blank"
      );
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const close = () => setOpenAction(null);
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, []);

  return (
    <div className="p-6 min-h-screen bg-slate-50/50">

      {/* HEADER */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">National Stock Card</h1>
            <p className="text-sm text-slate-500 mt-1">
              Monitor stok, distribusi, replenishment & performa inventory
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="h-[45px] border border-slate-200 rounded-2xl px-4 flex items-center gap-2 bg-white">
              <CalendarDays size={16} className="text-slate-500" />
              <input
                type="date"
                value={tanggalStok}
                onChange={(e) => setTanggalStok(e.target.value)}
                className="text-sm font-medium outline-none bg-transparent"
              />
            </div>

            <button
              onClick={getInventory}
              disabled={loading}
              className="h-[45px] px-4 rounded-2xl border border-slate-200 hover:bg-slate-50 flex items-center gap-2 bg-white disabled:opacity-50"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>

            <button
              onClick={handleExport}
              className="h-[45px] px-5 rounded-2xl bg-gradient-to-r from-indigo-600 to-blue-600 text-white flex items-center gap-2"
            >
              <Download size={16} />
              Export
            </button>
          </div>
        </div>

        <div className="h-px bg-slate-100 my-6" />

        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-slate-800">Summary</h3>
            <p className="text-xs text-slate-500 flex items-center gap-1.5 mt-0.5">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
              Tanggal Terakhir Audit: <strong className="text-slate-700 font-medium">{formattedLastAuditDate}</strong>
            </p>
          </div>

          <div className="bg-slate-100 rounded-xl p-1 flex">
            <button
              onClick={() => setViewMode("summary")}
              className={`px-4 py-2 text-sm rounded-lg transition-all ${
                viewMode === "summary" ? "bg-white shadow text-indigo-600 font-medium" : "text-slate-500"
              }`}
            >
              Stock Position
            </button>
            <button
              onClick={() => setViewMode("today")}
              className={`px-4 py-2 text-sm rounded-lg transition-all ${
                viewMode === "today" ? "bg-white shadow text-indigo-600 font-medium" : "text-slate-500"
              }`}
            >
              Today Activity
            </button>
          </div>
        </div>

        {/* STATS SUMMARY */}
        <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-12 gap-3">
          <AnimatePresence mode="wait">
            <motion.div
              key={viewMode}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="contents"
            >
              {(viewMode === "summary" ? summaryStats : todayStats).map((item, i) => (
                <div
                  key={i}
                  onClick={() => handleCardExport(item.type)}
                  className={`rounded-2xl bg-slate-50 border border-slate-100 px-4 py-4 transition-all ${
                    item.type ? "cursor-pointer hover:bg-white hover:shadow-md active:scale-95" : ""
                  }`}
                >
                  <div className={`w-8 h-1 rounded-full mb-3 bg-gradient-to-r ${item.color}`} />
                  <p className="text-[11px] text-slate-500 font-medium">{item.label}</p>
                  <h2 className="mt-2 text-lg font-bold text-slate-800">{item.value}</h2>
                </div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* RANCANGAN TABEL */}
      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-5 bg-slate-50/70 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <ClipboardList size={18} className="text-indigo-600" />
            <h3 className="text-sm font-bold text-slate-800">Rincian Pergerakan Per Artikel (`cutoff_md`)</h3>
            <span className="text-[10px] bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full font-medium ml-1">Klik baris untuk detail</span>
          </div>
          
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
            <input
              type="text"
              placeholder="Cari Artikel, Barcode, Dept..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full h-9 pl-9 pr-4 text-xs font-medium border border-slate-200 rounded-xl outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-white"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-[11px] whitespace-nowrap">
            <thead>
              <tr className="bg-slate-100/80 text-slate-600 font-semibold border-b border-slate-200 uppercase tracking-wider text-[10px]">
                <th className="p-3 pl-5 sticky left-0 bg-slate-100 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] min-w-[200px]">Artikel / Barcode</th>
                <th className="p-3">Dept</th>
                <th className="p-3">Prinsipal</th>
                <th className="p-3">Gender / Size</th>
                <th className="p-3 text-center bg-slate-50 font-bold border-x border-slate-200/60">Stok Audit</th>
                <th className="p-3 text-center text-emerald-600">SJ Msk</th>
                <th className="p-3 text-center text-emerald-600">Mut Msk</th>
                <th className="p-3 text-center text-emerald-600">PBM</th>
                <th className="p-3 text-center text-emerald-600">Sz In</th>
                <th className="p-3 text-center text-rose-600">Jual</th>
                <th className="p-3 text-center text-rose-600">Mut Klr</th>
                <th className="p-3 text-center text-rose-600">Ret Whs</th>
                <th className="p-3 text-center text-rose-600">Ret Prm</th>
                <th className="p-3 text-center text-rose-600">PBK</th>
                <th className="p-3 text-center text-rose-600">Sz Out</th>
                <th className="p-3 text-center bg-indigo-50 font-bold border-l border-slate-200">Stok Akhir</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {paginatedData.length > 0 ? (
                paginatedData.map((item, index) => (
                  <tr 
                    key={item.id || item.barcode || index} 
                    onClick={() => setSelectedProduct(item)} // Trigger Popup onClick
                    className="hover:bg-indigo-50/40 cursor-pointer transition-colors text-slate-700 group"
                  >
                    <td className="p-3 pl-5 sticky left-0 bg-white font-medium shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] group-hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg overflow-hidden border border-slate-100 bg-slate-50 flex-shrink-0 flex items-center justify-center">
                          <img
                            src={`https://asset-3s.3second.co.id/product/${item?.kode_extend || item?.artikel}_1.JPG`}
                            alt={item?.artikel}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => {
                              e.target.onerror = null; 
                              e.target.src = "https://placehold.co/150x150?text=No+Image";
                            }}
                          />
                        </div>
                        <div>
                          <div className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">{item?.artikel}</div>
                          <div className="text-[10px] text-slate-400 font-mono mt-0.5">{item?.barcode}</div>
                        </div>
                      </div>
                    </td>

                    <td className="p-3 text-slate-500">{item?.dept}</td>
                    <td className="p-3 text-slate-500 max-w-[120px] truncate">{item?.prinsipal}</td>
                    <td className="p-3 text-slate-600 font-medium">
                      {item?.gender || "-"} / <span className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-800 text-[10px]">{item?.ukuran}</span>
                    </td>
                    <td className="p-3 text-center font-bold bg-slate-50/30 border-x border-slate-200/60 text-slate-800">
                      {Number(item?.stok_audit || 0).toLocaleString()}
                    </td>
                    <td className="p-3 text-center text-slate-600">{Number(item?.sj_masuk || 0) || "-"}</td>
                    <td className="p-3 text-center text-slate-600">{Number(item?.mutasi_masuk || 0) || "-"}</td>
                    <td className="p-3 text-center text-slate-600">{Number(item?.pbm_rebarcode || 0) || "-"}</td>
                    <td className="p-3 text-center text-slate-600">{Number(item?.tukar_size_in || 0) || "-"}</td>
                    <td className="p-3 text-center text-slate-900 font-bold">{Number(item?.penjualan || 0) || "-"}</td>
                    <td className="p-3 text-center text-slate-600">{Number(item?.mutasi_keluar || 0) || "-"}</td>
                    <td className="p-3 text-center text-slate-600">{Number(item?.ret_whs || 0) || "-"}</td>
                    <td className="p-3 text-center text-slate-600">{Number(item?.ret_promosi || 0) || "-"}</td>
                    <td className="p-3 text-center text-slate-600">{Number(item?.ret_rebarcode || 0) || "-"}</td>
                    <td className="p-3 text-center text-slate-600">{Number(item?.tukar_size_out || 0) || "-"}</td>
                    <td className="p-3 text-center bg-indigo-50/30 font-bold text-indigo-600 border-l border-slate-200">
                      {Number(item?.stok_akhir || 0).toLocaleString()}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="16" className="p-8 text-center text-slate-400 bg-white">
                    Data rincian artikel tidak ditemukan atau kosong.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Panel Footer Pagination */}
        {totalPages > 1 && (
          <div className="p-4 bg-slate-50/50 border-t border-slate-200 flex items-center justify-between text-xs text-slate-600 font-medium">
            <div>
              Menampilkan <span className="text-slate-800 font-bold">{Math.min(filteredArtikel.length, (currentPage - 1) * itemsPerPage + 1)}</span> - <span className="text-slate-800 font-bold">{Math.min(filteredArtikel.length, currentPage * itemsPerPage)}</span> dari <span className="text-slate-800 font-bold">{filteredArtikel.length}</span> Artikel
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="w-7 h-7 rounded-lg border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50 disabled:opacity-40 transition-all"
              >
                <ChevronLeft size={14} />
              </button>
              <div className="px-2">Halaman <span className="text-slate-800 font-bold">{currentPage}</span> dari <span className="text-slate-800 font-bold">{totalPages}</span></div>
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="w-7 h-7 rounded-lg border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50 disabled:opacity-40 transition-all"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ======================================================== */}
      {/* MODAL DETAIL STOK POPUP (ANIME PRESENCE)                 */}
      {/* ======================================================== */}
    <AnimatePresence>
    {selectedProduct && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop Blur overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setSelectedProduct(null)}
          className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        />

        {/* Modal Card content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", duration: 0.4 }}
          className="bg-white rounded-3xl shadow-xl border border-slate-100 max-w-lg w-full overflow-hidden relative z-10 flex flex-col"
        >
          {/* Header Modal */}
          <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <div className="flex items-center gap-2.5">
              <div className="p-2 bg-indigo-50 rounded-xl text-indigo-600">
                <Store size={18} />
              </div>
              <div>
                <h3 className="font-bold text-slate-800 text-sm">Detail Informasi Stok</h3>
                <p className="text-[11px] text-slate-400 font-medium">Kode Toko: {localStorage.getItem("kode_toko") || "-"}</p>
              </div>
            </div>
            <button 
              onClick={() => setSelectedProduct(null)}
              className="w-8 h-8 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-all"
            >
              <X size={15} />
            </button>
          </div>

          {/* Body Modal */}
          <div className="p-6 overflow-y-auto max-h-[75vh] space-y-5 custom-scrollbar">
            
            {/* Rincian Ringkas Produk */}
            <div className="flex gap-4 items-start pb-5 border-b border-slate-100">
              <div className="w-20 h-20 rounded-xl overflow-hidden border border-slate-200 bg-slate-50 flex-shrink-0 flex items-center justify-center shadow-inner">
                <img
                  src={`https://asset-3s.3second.co.id/product/${selectedProduct?.kode_extend || selectedProduct?.artikel}_1.JPG`}
                  alt={selectedProduct?.artikel}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null; 
                    e.target.src = "https://placehold.co/150x150?text=No+Image";
                  }}
                />
              </div>
              <div className="flex-1 space-y-1">
                <span className="text-[10px] uppercase font-bold tracking-wider text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
                  {selectedProduct?.dept || "GENERAL"}
                </span>
                <h4 className="text-base font-bold text-slate-900 leading-tight pt-1">{selectedProduct?.artikel}</h4>
                <p className="text-xs font-mono text-slate-400">{selectedProduct?.barcode}</p>
                <p className="text-xs text-slate-500 font-medium">
                  Prinsipal: <span className="text-slate-700">{selectedProduct?.prinsipal || "-"}</span>
                </p>
              </div>
            </div>

            {/* Grid Rincian Stok Utama */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-50 rounded-2xl p-3.5 border border-slate-100">
                <div className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Gender / Ukuran</div>
                <div className="text-sm font-bold text-slate-800 mt-1">
                  {selectedProduct?.gender || "-"} / <span className="text-indigo-600 font-mono">{selectedProduct?.ukuran || "-"}</span>
                </div>
              </div>

              <div className="bg-indigo-50/50 rounded-2xl p-3.5 border border-indigo-100/40">
                <div className="text-[10px] font-medium text-indigo-500 uppercase tracking-wider flex items-center gap-1">
                  <Layers size={11} /> Stok Akhir Available
                </div>
                <div className="text-xl font-black text-indigo-600 mt-1">
                  {Number(selectedProduct?.stok_akhir || 0).toLocaleString()} <span className="text-xs font-medium text-slate-400">Pcs</span>
                </div>
              </div>
            </div>

            {/* Flow Ringkasan Aktivitas Kartu Stok */}
            

            {/* SECTION BARU: Ketersediaan di Toko Lain */}
            <div className="pt-2">
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2.5">
    <h5 className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
      <Store size={14} className="text-indigo-600" />
      Lokasi Toko Teridentifikasi Stok (&gt; 0):
    </h5>
    
    {/* INPUT SEARCH NAMA TOKO */}
    {storeStockList.length > 0 && (
      <div className="relative w-full sm:w-48">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" size={12} />
        <input
          type="text"
          placeholder="Cari nama toko..."
          value={searchStoreQuery}
          onChange={(e) => setSearchStoreQuery(e.target.value)}
          className="w-full h-7 pl-7 pr-3 text-[11px] font-medium border border-slate-200 rounded-lg outline-none focus:border-indigo-500 bg-white"
        />
      </div>
    )}
  </div>
  
  <div className="bg-slate-50 rounded-2xl border border-slate-100 overflow-hidden">
    {loadingStore ? (
      <div className="p-8 text-center text-xs text-slate-400 flex flex-col items-center gap-2">
        <RefreshCw size={16} className="animate-spin text-indigo-500" />
        Menyinkronkan data stok toko...
      </div>
    ) : filteredStoreStockList.length > 0 ? (
      <div className="max-h-[180px] overflow-y-auto divide-y divide-slate-200/60 custom-scrollbar">
       <table className="w-full text-left border-collapse text-[11px]">
  <thead className="bg-slate-100 text-slate-500 font-semibold uppercase sticky top-0 z-10 shadow-sm">
    <tr>
      <th className="p-2.5 pl-4">Nama Toko</th>
      <th className="p-2.5 text-center" width="80">Stok Akhir</th>
      <th className="p-2.5 text-center" width="220">Aksi Request Produk</th>
    </tr>
  </thead>
  <tbody className="divide-y divide-slate-100 bg-white">
    {filteredStoreStockList.map((store, idx) => (
      <tr 
        key={idx} 
        onClick={() => setSelectedStoreHistory(store)} // Tetap bisa klik baris untuk melihat History log
        className="hover:bg-slate-50/80 cursor-pointer transition-colors text-slate-700 group"
      >
        {/* Kolom Nama Toko */}
        <td className="p-2.5 pl-4 font-medium text-slate-800 group-hover:text-indigo-600 transition-colors">
          {store.nama_toko}
        </td>

        {/* Kolom Indikator Sisa Stok */}
        <td className="p-2.5 text-center font-bold text-indigo-600 bg-indigo-50/10">
          {Number(store.stok_akhir).toLocaleString()}
        </td>

        {/* KOLOM AKSI: Munculkan form input hanya jika stok tersedia */}
       <td className="p-2 text-center" onClick={(e) => e.stopPropagation()}>
  {Number(store.stok_akhir) > 0 ? (
    <form 
      onSubmit={(e) => handleSendRequest(e, store)}
      className="flex items-center gap-1.5 justify-end"
    >
      {/* Dropdown Kategori Berdasarkan Kode Toko */}
      <select
        value={requestKategori[store.kode_toko] || "Direct Sales"}
        onChange={(e) => setRequestKategori(prev => ({ 
          ...prev, 
          [store.kode_toko]: e.target.value 
        }))}
        className="h-7 px-1.5 text-[10px] font-semibold border border-slate-200 rounded-lg bg-white text-slate-700 outline-none focus:border-indigo-500"
      >
        <option value="Direct Sales">Direct Sales</option>
        <option value="Percepatan Distribusi">Percepatan Distribusi</option>
      </select>

      {/* Input Qty Berdasarkan Kode Toko */}
      <input
        type="number"
        placeholder="Qty"
        min="1"
        max={store.stok_akhir}
        value={requestQty[store.kode_toko] || ""}
        onChange={(e) => setRequestQty(prev => ({ 
          ...prev, 
          [store.kode_toko]: e.target.value 
        }))}
        required
        className="w-12 h-7 px-1 text-center font-bold text-slate-800 border border-slate-200 rounded-lg outline-none focus:border-indigo-500"
      />

      <button
        type="submit"
        disabled={submittingRequest}
        className="h-7 px-2.5 text-[10px] font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg shadow-xs transition-all disabled:opacity-50"
      >
        Minta
      </button>
    </form>
  ) : (
    <span className="text-xs text-slate-400 font-medium italic">Stok Habis</span>
  )}
</td>
      </tr>
    ))}
  </tbody>
</table>
      </div>
    ) : (
      <div className="p-6 text-center text-xs text-slate-400 font-medium">
        {storeStockList.length > 0 ? "Nama toko tidak ditemukan." : "Stok tidak ditemukan atau kosong di toko retail nasional lainnya."}
      </div>
    )}
  </div>

  {/* ======================================================== */}
  {/* NESTED MODAL: DETAIL HISTORY STOK TOKO YANG DIKLIK        */}
  {/* ======================================================== */}
  <AnimatePresence>
    {selectedStoreHistory && (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
        {/* Backdrop Hitam Transparan */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setSelectedStoreHistory(null)}
          className="absolute inset-0 bg-slate-950/50 backdrop-blur-xs"
        />

        {/* Konten Modal History */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 15 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 15 }}
          className="bg-white rounded-2xl shadow-2xl border border-slate-100 max-w-md w-full overflow-hidden relative z-10 flex flex-col"
        >
          {/* Header Sub-Modal */}
          <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
            <div>
              <h4 className="font-bold text-slate-800 text-xs">Kartu Stok History Toko</h4>
              <p className="text-[10px] text-indigo-600 font-semibold mt-0.5">{selectedStoreHistory.nama_toko}</p>
            </div>
            <button 
              onClick={() => setSelectedStoreHistory(null)}
              className="w-6 h-6 rounded-full border border-slate-200 bg-white flex items-center justify-center text-slate-400 hover:text-slate-600"
            >
              <X size={12} />
            </button>
          </div>

          {/* Body Sub-Modal */}
          <div className="p-4 overflow-y-auto max-h-[300px]">
            {loadingHistory ? (
              <div className="p-8 text-center text-xs text-slate-400 flex flex-col items-center gap-2">
                <RefreshCw size={14} className="animate-spin text-indigo-500" />
                Memuat rincian aktivitas...
              </div>
            ) : historyStockList.length > 0 ? (
              <div className="space-y-2">
                {/* Petunjuk struktur data: Ambil record history dari database cutoff_md */}
                {historyStockList.map((hist, i) => (
                  <div key={i} className="bg-slate-50 border border-slate-100 rounded-xl p-3 space-y-2 text-[11px]">
                    <div className="flex justify-between border-b border-slate-200/60 pb-1.5 font-medium">
                      <span className="text-slate-500">Stok Awal Audit</span>
                      <span className="text-slate-800 font-bold">{hist.stok_audit || 0} Pcs</span>
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-slate-600">
                      <div className="flex justify-between text-emerald-600"><span>SJ Masuk:</span> <span>+{hist.sj_masuk || 0}</span></div>
                      <div className="flex justify-between text-rose-600"><span>Penjualan:</span> <span>-{hist.penjualan || 0}</span></div>
                      <div className="flex justify-between text-emerald-600"><span>Mutasi In:</span> <span>+{hist.mutasi_masuk || 0}</span></div>
                      <div className="flex justify-between text-rose-600"><span>Mutasi Out:</span> <span>-{hist.mutasi_keluar || 0}</span></div>
                      <div className="flex justify-between text-emerald-600"><span>PBM:</span> <span>+{hist.pbm_rebarcode || 0}</span></div>
                      <div className="flex justify-between text-rose-600"><span>Ret Whs:</span> <span>-{hist.ret_whs || 0}</span></div>
                    </div>
                    <div className="flex justify-between items-center pt-1.5 border-t border-slate-200/60 font-bold text-slate-800">
                      <span>Sisa Stok Akhir</span>
                      <span className="bg-indigo-600 text-white px-2 py-0.5 rounded text-[10px]">{hist.stok_akhir || 0} Pcs</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-6 text-center text-xs text-slate-400">
                Tidak ada data pergerakan logistik di toko ini.
              </div>
            )}
          </div>

          {/* Footer Sub-Modal */}
          <div className="p-3 bg-slate-50 border-t border-slate-100 flex justify-end">
            <button
              onClick={() => setSelectedStoreHistory(null)}
              className="px-4 h-7 bg-slate-800 hover:bg-slate-900 text-white font-medium text-[10px] rounded-lg shadow-xs"
            >
              Kembali
            </button>
          </div>
        </motion.div>
      </div>
    )}
  </AnimatePresence>
</div>

          </div>

          {/* Footer Button Close */}
          <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end">
            <button
              onClick={() => setSelectedProduct(null)}
              className="px-5 h-9 bg-slate-800 hover:bg-slate-900 text-white font-medium text-xs rounded-xl transition-all shadow-sm"
            >
              Selesai
            </button>
          </div>
        </motion.div>
      </div>
    )}
  </AnimatePresence>

    </div>
  );
}